import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-hot-toast';
import { JobStage } from 'services/api/jobStage';
import { Job, updateJobStage, UpdateJobStageDto } from 'services/api/job';
import { JOB_STAGES, STAGE_MAP } from 'constants/jobStage';
import { Link } from 'react-router-dom';
import { CommentType, CREATE_COMMENT } from 'services/api/comment';
import { useAccessToken } from 'providers/AccessTokenProvider';

interface ChangeJobStageModalProps {
  show: boolean;
  onHide: () => void;
  job: Job;
  jobStages: JobStage[];
}

export const ChangeJobStageModal: React.FC<ChangeJobStageModalProps> = ({
  show,
  onHide,
  job,
  jobStages
}) => {
  const [selectedStage, setSelectedStage] = useState<string>(job.jobStage.id);
  const [returnDate, setReturnDate] = useState<Date | null>(null);
  const [comment, setComment] = useState<string>('');
  const queryClient = useQueryClient();
  const { authUser: user } = useAccessToken();
  const userId = user?._id || user?.id;

  useEffect(() => {
    if (show && job.returnDate) {
      setReturnDate(new Date(job.returnDate));
    } else {
      setReturnDate(null);
    }
    setSelectedStage(job.jobStage.id);
    setComment('');
  }, [show, job]);

  const selectedJobStage = jobStages.find(stage => stage.id === selectedStage);

  const isEquipmentReturnedStage =
    selectedJobStage?.name.toLowerCase() === JOB_STAGES.EQUIPMENT_RETURNED ||
    selectedJobStage?.name.toLowerCase() === JOB_STAGES.EQUIPMENT_RETURN ||
    selectedJobStage?.name.toLowerCase() ===
      STAGE_MAP['Equipment Returned'].toLowerCase();

  const showReturnDate = isEquipmentReturnedStage;

  const minReturnDate = new Date();
  minReturnDate.setDate(minReturnDate.getDate() - 30);
  const maxReturnDate = new Date();

  const { mutate, isLoading } = useMutation(updateJobStage, {
    onSuccess: () => {
      queryClient.invalidateQueries('getJobs');
      queryClient.invalidateQueries('getAllActivityLogs');

      if (comment.trim()) {
        createComment.mutate({
          content: comment,
          jobId: (job.id || job._id) as string,
          creator: userId as string,
          commentType: CommentType.JobStage
        });
      } else {
        toast.success('Job stage updated successfully');
        onHide();
        setComment('');
        setReturnDate(null);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update job stage');
    }
  });

  const createComment = useMutation('createComment', CREATE_COMMENT, {
    onSuccess: () => {
      queryClient.invalidateQueries('getJobs');
      toast.success('Job stage updated and comment added successfully');
      onHide();
      setComment('');
      setReturnDate(null);
    },
    onError: (error: any) => {
      toast.error(
        error.message || 'Job stage updated but failed to add comment'
      );
      onHide();
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!userId) {
      return toast.error('User information not found');
    }

    if (!selectedJobStage) {
      return toast.error('Invalid stage selected');
    }

    if (isEquipmentReturnedStage && !returnDate) {
      toast.error('Please select a return date for equipment returned stage');
      return;
    }

    const utcReturnDate = returnDate
      ? new Date(
          Date.UTC(
            returnDate.getFullYear(),
            returnDate.getMonth(),
            returnDate.getDate(),
            returnDate.getHours(),
            returnDate.getMinutes(),
            0,
            0
          )
        ).toISOString()
      : null;

    const updateData: UpdateJobStageDto = {
      jobId: { _id: (job?._id || job?.id) as string },
      stageId: selectedStage.toString(),
      userId: userId.toString(),
      ...(comment && { comment })
    };

    if (returnDate && utcReturnDate) {
      updateData.returnDate = new Date(utcReturnDate);
    }

    mutate(updateData);
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Change Job Stage</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Current Stage */}
          <div className="mb-3">
            <strong>Current Stage: </strong>
            <span className="text-primary">{job.jobStage.name}</span>
          </div>

          {/* Stage Selection */}
          <Form.Group className="mb-3">
            <Form.Label>New Stage</Form.Label>
            <Form.Select
              value={selectedStage}
              onChange={e => setSelectedStage(e.target.value)}
              required
            >
              {jobStages.map(stage => (
                <option key={stage.id} value={stage.id}>
                  {stage.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Return Date for Equipment Returned */}
          {showReturnDate && (
            <Form.Group className="mb-3">
              <Form.Label>Returned Date</Form.Label>
              <div>
                <DatePicker
                  selected={returnDate}
                  onChange={(date: Date | null) => setReturnDate(date)}
                  dateFormat="MMMM d, yyyy"
                  minDate={minReturnDate}
                  maxDate={maxReturnDate}
                  value={returnDate?.toLocaleDateString('en-US')}
                  className="form-control"
                  placeholderText="Select return date"
                  required
                  showMonthYearPicker={false}
                  showFullMonthYearPicker={true}
                />
              </div>
              <Form.Text className="text-muted">
                Select a date between the last 30 days and today
              </Form.Text>
            </Form.Group>
          )}

          {/* Comment Field */}
          <Form.Group className="mb-3">
            <Form.Label>Job Stage Comment</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Add a job stage comment..."
            />
            <Form.Text className="text-muted">
              This comment will be posted as a Job Stage comment on this job
            </Form.Text>
          </Form.Group>

          {/* Stage Change Preview */}
          <div className="mt-3 p-2 bg-light rounded">
            <small className="text-muted">
              Changing from <strong>{job.jobStage.name}</strong> to{' '}
              <strong>{selectedJobStage?.name}</strong>
              {showReturnDate && returnDate && (
                <>
                  {' '}
                  with return date set to{' '}
                  <strong>{returnDate.toLocaleDateString()}</strong>
                </>
              )}
            </small>
          </div>

          <div className="mt-3 text-end">
            <Link
              to={`/home/apps/job/project-list-view?tab=activityLogs&jobId=${job._id}`}
              className="text-primary"
            >
              View job activity logs →
            </Link>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? 'Updating...' : 'Update Stage'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
